import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { cn } from "src/lib/utils";

interface AuthFieldProps extends React.ComponentProps<"input"> {
	label: string;
	error?: string | false;
	/** Renders a show/hide toggle and manages the input type. */
	isPassword?: boolean;
}

/** Labelled input for the auth screens, with inline (not toast-only) validation — spec §6. */
const AuthField = ({ label, error, isPassword, name, className, ...props }: AuthFieldProps) => {
	const [visible, setVisible] = useState(false);
	const errorId = error ? `${name}-error` : undefined;

	return (
		<div className="tw-space-y-1.5">
			<Label htmlFor={name}>{label}</Label>
			<div className="tw-relative">
				<Input
					id={name}
					name={name}
					aria-invalid={Boolean(error)}
					aria-describedby={errorId}
					className={cn(error && "tw-border-red focus-visible:tw-border-red focus-visible:tw-ring-red/20", isPassword && "tw-pr-10", className)}
					{...props}
					type={isPassword ? (visible ? "text" : "password") : props.type}
				/>
				{isPassword && (
					<button
						type="button"
						onClick={() => setVisible((prev) => !prev)}
						aria-label={visible ? "Hide password" : "Show password"}
						className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-rounded-lg tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text"
					>
						{visible ? <EyeOff className="tw-h-4 tw-w-4" /> : <Eye className="tw-h-4 tw-w-4" />}
					</button>
				)}
			</div>
			{error && (
				<p id={errorId} className="tw-text-[12.5px] tw-text-red">
					{error}
				</p>
			)}
		</div>
	);
};

export default AuthField;
